"use client";

import { useState } from "react";
import { Plus, Trash2, Edit2, ChevronUp, ChevronDown, X, Check } from "lucide-react";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import { Button } from "@/components/atoms/Button";
import { validateFields } from "@/lib/utils";
import { showValidationErrorToast } from "../../form3/utils";

interface JsonListSectionProps {
  title: string;
  icon: string;
  items: any[];
  fieldsConfig: any[];
  onUpdate: (items: any[]) => void;
}

const JsonListSection = ({ title, icon, items, fieldsConfig, onUpdate }: JsonListSectionProps) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formFields, setFormFields] = useState<Record<string, any>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [fieldMessages, setFieldMessages] = useState<Record<string, string>>({});

  const buildDefaultFields = () => {
    const defaults: Record<string, any> = {};
    fieldsConfig.forEach((field: any) => {
      defaults[field.fieldName] = field.defaultValue ?? field.value ?? "";
    });
    return defaults;
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingIndex(null);
    const defaults = buildDefaultFields();
    // Auto-increment priority based on existing items
    if ("priority" in defaults) {
      const maxPriority = items.reduce((max, item) => Math.max(max, item.priority ?? 0), 0);
      defaults.priority = maxPriority + 1;
    }
    setFormFields(defaults);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setIsAdding(false);
    const item = items[index];
    const fields: Record<string, any> = {};
    fieldsConfig.forEach((field: any) => {
      fields[field.fieldName] = item[field.fieldName] ?? field.defaultValue ?? "";
    });
    setFormFields(fields);
  };

  const handleSave = () => {
    // Validate required fields before saving
    const { errors, messages, hasErrors } = validateFields(fieldsConfig, formFields);
    setFieldErrors(errors);
    setFieldMessages(messages);
    if (hasErrors) {
      showValidationErrorToast();
      return;
    }

    const newItems = [...items];
    if (isAdding) {
      newItems.push({ ...formFields, priority: newItems.length + 1 });
    } else if (editingIndex !== null) {
      newItems[editingIndex] = { ...newItems[editingIndex], ...formFields };
    }
    onUpdate(newItems);
    setIsAdding(false);
    setEditingIndex(null);
    setFormFields({});
    setFieldErrors({});
    setFieldMessages({});
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingIndex(null);
    setFormFields({});
    setFieldErrors({});
    setFieldMessages({});
  };

  const handleDelete = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    // Re-index priorities
    const reindexed = newItems.map((item, i) => ({ ...item, priority: i + 1 }));
    onUpdate(reindexed);
    if (editingIndex === index) {
      setEditingIndex(null);
      setFormFields({});
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    const reindexed = newItems.map((item, i) => ({ ...item, priority: i + 1 }));
    onUpdate(reindexed);
  };

  const handleMoveDown = (index: number) => {
    if (index >= items.length - 1) return;
    const newItems = [...items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    const reindexed = newItems.map((item, i) => ({ ...item, priority: i + 1 }));
    onUpdate(reindexed);
  };

  // Get display label for an item
  const getItemLabel = (item: any) => {
    return item.name || item.alt_image || item.alt_icon || `Item ${item.priority || ""}`;
  };

  const isEditing = isAdding || editingIndex !== null;

  return (
    <div className="border-t pt-6 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold flex items-center gap-2">
          <span>{icon}</span> {title}
        </h4>
        {!isEditing && (
          <button
            onClick={handleAdd}
            className="flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        )}
      </div>

      {/* Items List */}
      {items.length > 0 && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 mb-3 max-h-60 overflow-y-auto">
          {items.map((item, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-2 rounded mb-1 last:mb-0 ${
                editingIndex === index ? "bg-blue-50 border border-blue-200" : "bg-white hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-xs text-gray-400 w-5 text-center">{index + 1}</span>
                {item.image && (
                  <img src={item.image} alt={item.alt_image || ""} className="w-8 h-8 rounded object-cover" />
                )}
                {item.icon && (
                  <img src={item.icon} alt={item.alt_icon || ""} className="w-6 h-6 rounded" />
                )}
                <span className="text-sm truncate">{getItemLabel(item)}</span>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button onClick={() => handleMoveUp(index)} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleMoveDown(index)} disabled={index >= items.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleEdit(index)} className="p-1 text-blue-500 hover:text-blue-700">
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(index)} className="p-1 text-red-500 hover:text-red-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && !isEditing && (
        <div className="text-center py-4 text-gray-400 text-sm bg-gray-50 rounded-lg border border-gray-200">
          No items yet. Click "Add" to add one.
        </div>
      )}

      {/* Edit/Add Form */}
      {isEditing && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white overflow-hidden min-w-0">
          <h5 className="text-sm font-medium mb-3">
            {isAdding ? `Add ${title.replace(/s$/, "")}` : `Edit ${title.replace(/s$/, "")}`}
          </h5>
          <div className="json-list-fields overflow-hidden min-w-0 w-full">
            <style>{`.json-list-fields .min-w-80 { min-width: 0 !important; } .json-list-fields .max-w-md { max-width: 100% !important; }`}</style>
            <FieldsFormatter
              data={formFields}
              fields={fieldsConfig}
              updateParent={true}
              onChange={setFormFields}
              validationErrors={fieldErrors}
              validationMessages={fieldMessages}
              className="!p-0 bg-transparent max-w-full"
            />
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={handleSave} classType="admin" className="flex items-center gap-1">
              <Check className="w-3.5 h-3.5" />
              {isAdding ? "Add" : "Update"}
            </Button>
            <Button onClick={handleCancel} classType="admin" className="px-4 bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center gap-1">
              <X className="w-3.5 h-3.5" />
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JsonListSection;
